import { QueryFactory } from '@lmt/shared-types';
import { ResourceDto, ResourceType } from '@lmt/shared-types';

import { TableTitle } from '@lmt/unicorn-shared/app/components/common/table/TableTitle';
import {
  Button,
  ButtonColor,
} from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { useGlobalForms } from '@lmt/unicorn-shared/app/components/global-forms/useGlobalForms';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import React, { FC, useMemo } from 'react';
import { ResourcesTable } from './components/ResourcesTable';

import tableStyles from '@lmt/unicorn-shared/app/components/common/table/TableTitle.module.scss';
import AddIcon from '@lmt/unicorn-shared/app/icons/plus-blue.svg?react';
import { Loading } from '@lmt/unicorn-shared/app/components/common/Loading';
import { useEventCallback } from '@mui/material';
import { useInsertKeyHandler } from '../../dictionaries/useInsertKeyHandler';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';
import SettingsIcon from '@lmt/unicorn-shared/app/icons/gear.svg?react';
import { IconButton } from '../../common/ui-kit/IconButton';
import styles from './ResourceList.module.scss';
import { useResourceManualOrdering } from '@lmt/unicorn-shared/app/api/custom-hooks';
import { sortResources } from '@lmt/unicorn-shared/app/helpers/sort';

interface Props {
  resourceType: ResourceType;
}

function getTranslationScope(type: ResourceType) {
  switch (type) {
    case ResourceType.Places:
      return 'resources.place.list';
    case ResourceType.SpecialConditions:
      return 'resources.special_conditions.list';
    case ResourceType.Doctors:
      return 'resources.doctors.list';
    default:
      throw new Error('Unknown type');
  }
}

const ResourceList: FC<Props> = (props) => {
  const { resourceType } = props;
  const forms = useGlobalForms();
  const translationScope = getTranslationScope(resourceType);
  const i18next = useScopedTranslation(translationScope);
  const modals = useModal();
  const claims = useClaims();

  const resourcesQuery = QueryFactory.ResourceQuery.useGetResourcesQuery({
    includeDeleted: false,
    resourceType: props.resourceType,
  });

  const manualOrdering = useResourceManualOrdering(resourceType);

  const data = useMemo(() => {
    if (manualOrdering) {
      return (
        resourcesQuery.data?.data.sort(
          (a, b) => a.manualOrderPosition - b.manualOrderPosition,
        ) ?? []
      );
    }
    return resourcesQuery.data?.data.sort(sortResources) ?? [];
  }, [resourcesQuery.data?.data, manualOrdering]);

  const openEditing = useEventCallback((resource: ResourceDto) => {
    forms.openResourceEditing(resource, i18next.t('edit'));
  });

  const openCreating = useEventCallback((resourceType: ResourceType) => {
    forms.openResourceCreating(resourceType, i18next.t('create'));
  });

  const removeResource = useEventCallback(async (resource: ResourceDto) => {
    const isConfirmed = await modals.showDeletionConfirmation({
      text: i18next.t('confirm_message_remove'),
    });
    if (isConfirmed) {
      try {
        await QueryFactory.ResourceClient.removeResource(resource.id);
      } catch (error: any) {
        if (error.status === 409) {
          await modals.showError({
            text: i18next.t('resource_is_busy_message'),
          });
        } else {
          await modals.showError({ text: convertToErrorString(error) });
          throw error;
        }
      }
    }
  });

  useInsertKeyHandler(() => openCreating(props.resourceType));

  const onReorder = useEventCallback(
    async (item: ResourceDto, direction: 'up' | 'down') => {
      const isUp = direction == 'up';
      const currentPos = item.manualOrderPosition;
      const otherItem = (isUp ? data.reverse() : data).find((i) =>
        isUp
          ? i.manualOrderPosition < currentPos
          : i.manualOrderPosition > currentPos,
      );
      if (!otherItem) {
        console.error('Tried to swap places with undefined!');
        throw Error('Tried to swap places with undefined!');
      }
      const newPos = otherItem.manualOrderPosition;
      const prom1 = QueryFactory.ResourceClient.editResource(item.id, {
        name: item.name,
        manualOrderPosition: newPos,
      });
      const prom2 = QueryFactory.ResourceClient.editResource(otherItem.id, {
        name: otherItem.name,
        manualOrderPosition: currentPos,
      });
      await Promise.all([prom1, prom2]);
    },
  );

  return (
    <div
      className={tableStyles.pageContainer}
      data-test-id="table-page-container"
    >
      <div className={tableStyles.tableToolbar}>
        <TableTitle
          title={i18next.t('title')}
          count={resourcesQuery.data?.totalCount}
        />
        <Button
          testId="plus-button"
          title={i18next.t('add_button')}
          color={ButtonColor.Primary}
          onClick={() => openCreating(props.resourceType)}
          iconComponent={<AddIcon />}
        />
        {claims.permissions.manageResources && (
          <IconButton
            color="grey"
            testId="settings-icon"
            iconComponent={<SettingsIcon />}
            onClick={() => forms.openResourceSettings(props.resourceType)}
            className={styles.settingsButton}
          />
        )}
      </div>
      <div className={tableStyles.tableContainer}>
        <Loading loading={!resourcesQuery.data}>
          <ResourcesTable
            data={data}
            noResults={
              !!resourcesQuery.data && resourcesQuery.data.totalCount === 0
            }
            onDelete={removeResource}
            onEdit={openEditing}
            translationScope={translationScope}
            manualOrdering={manualOrdering}
            onReorder={onReorder}
          />
        </Loading>
      </div>
    </div>
  );
};

export default ResourceList;
